// "Update Code" confirmation + progress, shared by all three scopes (device,
// room, location) and both UI shells.
//
// The flow is deliberately three-stage — counting, confirming, sending —
// because this is not undoable and the devices reboot:
//
//   1. COUNT   gather the devices in scope, split live vs offline, and look up
//              what firmware each is on — all of them, offline included, since
//              an offline device is now instructed just like a live one.
//   2. CONFIRM ONE ROW PER DEVICE — name, firmware file, build date, when it was
//              last updated, and a tick box. Plus the exact payload, so the
//              operator sees the literal bytes before a fleet reflashes itself.
//   3. SEND    mark every selected row, then publish to the online ones, spaced,
//              with progress — a location rollout takes a while and silence
//              would look like a hang.
//
// ── THE SEND IS THREE ROUTES, NOT ONE ──────────────────────────────────────
// MQTT reaches a device that is connected at this second and nothing else, and
// most of the estate is asleep most of the time. So every selected device also
// gets `PendingCommand` (its ~20 minute poll) and `RequireUpdateCode` (its next
// Wi-Fi connect, and it survives a failed flash). Which route actually delivers
// is not knowable from here; setting all three is what makes "offline" a
// question of WHEN rather than WHETHER. The lifetimes and the firmware line
// numbers are in core/devices/firmwareUpdate.js.
//
// The dialog also takes marks back OFF, on the way in, for devices that have
// already finished — see `load`. A device normally clears its own mark, so
// anything found there is one that could not, and left alone it re-flashes on
// every reconnect for nothing.
//
// ── Why a table and not a summary ──────────────────────────────────────────
// The first version printed counts and a list of filenames. On a location with
// 39 devices that says "4 will update" without ever saying WHICH four, so the
// operator cannot check the one thing that matters before an irreversible
// action. Every device is now a row, and every row is individually selectable:
// the default selection is the safe one, but it is a default, not a decision
// taken on the operator's behalf.
//
// Offline rows are shown, not hidden. "33 offline — skipped" with no names is
// the same failure in miniature; the point of listing them is to notice the one
// that should not be offline.
//
// Rows are grouped the way the building is: COMMON DEVICES first — the gates,
// meters and locks that belong to the location rather than to any room — then
// one section per room. A flat list of thirty-nine devices is a list nobody
// reads; grouped, the operator can tick a whole room, and can see at a glance
// that (say) every device in Parking is offline.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box,
  LinearProgress, CircularProgress, Chip, Alert, Checkbox, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tooltip, Paper,
  FormControlLabel,
} from '@mui/material';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import { useApp } from '../../core/state/AppContext';
import {
  collectLocationDevices, collectRoomDevices, splitByLiveness,
  previewPayload, FIRMWARE_UPDATE_COMMAND, COMMON_ROOM_ID,
  // All three routes in one call, and the clear-down for the ones that have
  // already finished. See `run` and `load`.
  runFirmwareUpdate, clearFinishedUpdateMarks,
  PENDING_UPDATE_COMMAND, UPDATE_MARK_COLUMN,
} from '../../core/devices/firmwareUpdate';
import {
  firmwareInfoFor, CURRENT, STALE, BUILD_STAMP_TOLERANCE_MS,
} from '../../core/devices/firmwareInfo';

const unoOf = (d) => String((d && (d.UniqueNumber ?? d.uniqueNumber)) ?? '');
const nameOf = (d) => d?.DeviceName || d?.RoomName || unoOf(d) || 'Device';

// Rows are ordered by what the operator has to decide about, not alphabetically:
// the ones that need doing, then the ones nothing is known about, then the ones
// there is no reason to touch.
const RANK = { stale: 0, unknown: 1, current: 2 };

const STATE_CHIP = {
  stale: { label: 'Needs update', color: 'warning' },
  unknown: { label: 'Not known', color: 'default' },
  current: { label: 'Up to date', color: 'success' },
};

const SCOPE_LABEL = {
  device: 'device', room: 'room', location: 'location', list: 'selection',
};

// scope: { type: 'device'|'room'|'location'|'list', id, name, device?,
//          devices?, locationId? }
//
// A 'room' scope should carry locationId — it is what makes the room's device
// list complete (see core/devices/roomMerge.js).
//
// 'list' is an EXPLICIT set of device rows the caller already has, for screens
// whose devices are not a room or a location at all — the Tuya bench, whose
// list is "whatever spoke to the broker most recently" and belongs to no
// building. Everything downstream is device-driven, so the only difference is
// where the rows come from.
export default function UpdateCodeDialog({ open, scope, onClose }) {
  const { userId } = useApp();
  const [phase, setPhase] = useState('counting'); // counting | confirm | sending | done
  const [live, setLive] = useState([]);
  const [offline, setOffline] = useState([]);
  const [method, setMethod] = useState('mqtt');
  const [sent, setSent] = useState(0);
  // How many rows carry the two server-side routes after the run.
  const [marked, setMarked] = useState(0);
  const [markFailed, setMarkFailed] = useState(0);
  // Marks taken back off on load because the device had already finished.
  const [cleared, setCleared] = useState(0);
  const [info, setInfo] = useState(null);        // uno -> firmware facts
  const [selected, setSelected] = useState(() => new Set());
  // Two filters, both OFF by default. A location can be 33 offline devices out
  // of 39, and scrolling past all of them to reach the four that matter is the
  // complaint these answer — but hiding by default would decide for the
  // operator what they are allowed to notice.
  const [hideCurrent, setHideCurrent] = useState(false);
  const [hideOffline, setHideOffline] = useState(false);

  const load = useCallback(async () => {
    setPhase('counting');
    setSent(0);
    let devices = [];
    if (scope?.type === 'device') devices = scope.device ? [scope.device] : [];
    else if (scope?.type === 'list') devices = scope.devices || [];
    else if (scope?.type === 'room') {
      devices = await collectRoomDevices(userId, scope.id, scope.locationId, scope.name);
    } else if (scope?.type === 'location') {
      devices = await collectLocationDevices(userId, scope.id);
    }
    // Awaited: liveness is the same cached status the tiles show, so the dialog
    // and the dot can never disagree.
    const split = await splitByLiveness(devices);
    setLive(split.live);
    setOffline(split.offline);
    setMethod(split.method);
    // ⚠️ EVERY DEVICE, NOT JUST THE LIVE ONES — this used to ask only about the
    // ones that could be published to, on the reasoning that an offline device
    // was skipped whatever firmware it was on. That reasoning is gone: an
    // offline device is now marked and updates itself later, so its firmware
    // state decides whether it should be, and an offline row with no state
    // could not be judged at all.
    const facts = await firmwareInfoFor(devices).catch(() => null);
    setInfo(facts);
    // Default selection: everything not already on this build, online or not.
    // A device whose firmware could not be read is INCLUDED — see firmwareInfo,
    // CodeUpdated records an attempt rather than a success, so "don't know"
    // must not quietly mean "skip".
    setSelected(new Set(
      devices.map(unoOf).filter((u) => facts?.get(u)?.state !== CURRENT),
    ));
    setHideCurrent(false);
    setHideOffline(false);
    setPhase('confirm');

    // ── Tidy up after the ones that finished ────────────────────────────────
    // Not awaited, and deliberately after the screen is up: it is housekeeping,
    // not part of the answer, and a slow write must not hold the list back.
    // See clearFinishedUpdateMarks — the device normally does this itself, so
    // anything found here is a device that could not.
    clearFinishedUpdateMarks(facts).then((done) => {
      if (!done.length) return;
      setCleared(done.length);
      // Correct the rows in place. A chip still reading "marked" about a mark
      // this has just removed would send the operator after something gone.
      setInfo((prev) => {
        const next = new Map(prev);
        done.forEach((d) => {
          const f = next.get(d.uniqueNumber);
          if (f) next.set(d.uniqueNumber, { ...f, ...d });
        });
        return next;
      });
    }).catch(() => {});
  }, [scope, userId]);

  // ⚠️ KEYED ON WHAT THE SCOPE *IS*, not on the object's identity.
  //
  // Every caller passes an inline `scope={{ … }}` literal, which is a new object
  // on every render. Depending on it directly means: load -> setState -> render
  // -> new literal -> load again, forever. It has not bitten yet only because
  // the screens using it re-render rarely — the Tuya bench re-renders on every
  // MQTT frame, which is once a second on a live rack.
  //
  // The signature covers everything `load` reads, so a genuine scope change
  // still reloads.
  const scopeKey = useMemo(() => JSON.stringify({
    t: scope?.type ?? null,
    id: scope?.id ?? null,
    loc: scope?.locationId ?? null,
    dev: scope?.device ? unoOf(scope.device) : null,
    list: scope?.devices ? scope.devices.map(unoOf) : null,
  }), [scope]);

  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => { if (open) loadRef.current(); }, [open, scopeKey]);

  // One row per device, live and offline together.
  const rows = useMemo(() => {
    const build = (d, isOffline) => {
      const uno = unoOf(d);
      const f = info?.get(uno);
      // ⚠️ THE STATE IS THE FIRMWARE'S, NOT THE RADIO'S. It used to be forced to
      // 'offline' for any device that was not answering, which was honest while
      // offline meant "cannot be updated" — it no longer does. An offline device
      // is marked and updates itself later, so the question its row has to
      // answer is "does this need the new build", and liveness is shown
      // separately, next to the name.
      const state = f?.state || 'unknown';
      return {
        uno,
        name: nameOf(d),
        device: d,
        offline: isOffline,
        state,
        // Instructions already sitting on the row from an earlier attempt.
        // Without these the operator cannot tell a device nobody has touched
        // from one that has been marked for a month and never picked it up.
        marked: !!f?.updateMarked,
        pendingCommand: f?.pendingCommand || '',
        roomId: d?.RoomID ?? d?.RoomId ?? COMMON_ROOM_ID,
        roomName: String(d?.RoomName || '').trim(),
        binFile: f?.binFile || '',
        binUrl: f?.binUrl || '',
        builtAt: f?.builtAt ?? null,
        flashedAt: f?.flashedAt ?? null,
        withinTolerance: !!f?.withinTolerance,
      };
    };
    return [
      ...live.map((d) => build(d, false)),
      ...offline.map((d) => build(d, true)),
    ];
  }, [live, offline, info]);

  // What the filters leave on screen.
  //
  // ⚠️ EVERYTHING DOWNSTREAM COUNTS FROM THIS, not from `rows`. A hidden row
  // that stayed selected would be sent to without ever being shown — an
  // invisible instruction to reflash, on the one dialog that exists to make the
  // instruction visible. Hiding a row therefore also excludes it from the send.
  const visible = useMemo(() => rows.filter((r) => {
    if (hideOffline && r.offline) return false;
    if (hideCurrent && r.state === CURRENT) return false;
    return true;
  }), [rows, hideCurrent, hideOffline]);

  // Common devices first, then rooms by name. Within a section the ones that
  // need doing come first — with thirty offline devices in a location, sorting
  // a room alphabetically buries the two that matter.
  const groups = useMemo(() => {
    const byRoom = new Map();
    visible.forEach((r) => {
      const key = r.roomName ? `r:${r.roomName.toLowerCase()}` : 'common';
      if (!byRoom.has(key)) {
        byRoom.set(key, { key, title: r.roomName || 'Common devices', common: !r.roomName, rows: [] });
      }
      byRoom.get(key).rows.push(r);
    });
    const out = [...byRoom.values()];
    // Firmware state first, then the live ones ahead of the sleeping ones —
    // both are things to update, but one of them will have done it by the time
    // the dialog closes.
    out.forEach((g) => g.rows.sort(
      (a, b) => (RANK[a.state] - RANK[b.state])
        || (Number(a.offline) - Number(b.offline))
        || a.name.localeCompare(b.name),
    ));
    return out.sort((a, b) => {
      if (a.common !== b.common) return a.common ? -1 : 1;
      return a.title.localeCompare(b.title);
    });
  }, [visible]);

  /*
   * ── EVERY SELECTED DEVICE GETS ALL THREE ROUTES ────────────────────────────
   *
   * Offline rows used to be unselectable, and the dialog said so: "offline
   * devices cannot be sent to". That was only ever true of MQTT — publishing to
   * a device that is not connected goes nowhere, silently.
   *
   * There are two other routes, both server-side, and neither needs the device
   * to be awake when the button is pressed:
   *
   *     PendingCommand      picked up on the device's ~20 minute poll
   *     RequireUpdateCode   read on its next Wi-Fi connect, and it PERSISTS
   *                         until a flash actually succeeds
   *
   * So the split between live and offline no longer decides who is instructed —
   * every selected device is marked. It decides only who is ALSO published to,
   * because a publish to a sleeping device is UPDATE_STEP_DELAY_MS of
   * wall-clock spent on a message the broker drops.
   *
   * ⚠️ THE THREE ARE NOT INTERCHANGEABLE and the difference is worth saying out
   * loud in the UI: one has happened by the time the dialog closes, one is a
   * note the device may not read for twenty minutes, and one waits for a
   * power cycle that may be a week away.
   */
  const selectable = visible;
  const onScreen = new Set(visible.map((r) => r.uno));
  const chosen = (d) => selected.has(unoOf(d)) && onScreen.has(unoOf(d));
  // Everything that gets the two row writes...
  const allTargets = [...live, ...offline].filter(chosen);
  // ...and the subset that is also worth publishing to.
  const targets = live.filter(chosen);
  const queueTargets = offline.filter(chosen);
  const currentCount = rows.filter((r) => r.state === CURRENT).length;

  const toggle = (uno) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(uno)) next.delete(uno); else next.add(uno);
    return next;
  });

  const allSelected = selectable.length > 0 && selectable.every((r) => selected.has(r.uno));
  const someSelected = selectable.some((r) => selected.has(r.uno));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectable.map((r) => r.uno)));

  // A whole room at a time — the unit a rollout is actually planned in.
  const toggleGroup = (group) => {
    const ids = group.rows.map((r) => r.uno);
    if (!ids.length) return;
    const on = !ids.every((u) => selected.has(u));
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((u) => (on ? next.add(u) : next.delete(u)));
      return next;
    });
  };

  /*
   * The row writes first, then the MQTT walk — see runFirmwareUpdate for why
   * that order is not cosmetic. A run abandoned half way through the walk has
   * still instructed every device by the durable routes.
   *
   * ⚠️ A row that cannot be written is COUNTED, not thrown: one device that
   * will not update is not a reason to abandon the forty that already have. The
   * count is shown, so a partial run cannot read as a whole one.
   */
  const run = async () => {
    setPhase('sending');
    const out = await runFirmwareUpdate(allTargets, targets, userId, (p) => {
      setSent(p.sent || 0);
      setMarked(p.marked || 0);
      setMarkFailed(p.failed || 0);
    });
    setMarked(out.marked);
    setMarkFailed(out.failed);
    setSent(out.sent);
    setPhase('done');
  };

  const when = (ms) => (ms == null ? '—'
    : new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }));

  const scopeLabel = SCOPE_LABEL[scope?.type] || 'selection';

  const payload = previewPayload(
    scope?.type === 'device' ? (scope.device && scope.device.UniqueNumber) : null,
    userId,
  );

  const cell = { py: 0.75 };
  const mono = { fontFamily: 'monospace', fontWeight: 700, wordBreak: 'break-all' };

  return (
    <Dialog
      open={open}
      onClose={phase === 'sending' ? undefined : onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <SystemUpdateAltIcon />
        <Box>
          <Typography component="div" sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.2 }}>
            Update Code
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {scope?.name ? `${scope.name} · ${scopeLabel}` : scopeLabel}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {phase === 'counting' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">
              Asking the devices if they are there, and what firmware they are on…
            </Typography>
          </Box>
        )}

        {phase === 'confirm' && (
          <>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5, alignItems: 'center' }}>
              <Chip
                color={allTargets.length ? 'warning' : 'default'}
                size="small"
                label={`${allTargets.length} selected`}
              />
              {currentCount > 0 && (
                <Chip size="small" color="success" variant="outlined" label={`${currentCount} up to date`} />
              )}
              {offline.length > 0 && (
                <Chip size="small" variant="outlined" label={`${offline.length} offline`} />
              )}
              {/* Housekeeping done on the way in, reported rather than hidden:
                  a device that had finished and was still marked would have
                  re-flashed on its next reboot for nothing. */}
              {cleared > 0 && (
                <Tooltip title={`These devices were already on the current build but still carried ${UPDATE_MARK_COLUMN}. Left alone they would update again on their next Wi-Fi connect.`}>
                  <Chip size="small" color="info" variant="outlined" label={`${cleared} finished mark${cleared === 1 ? '' : 's'} cleared`} />
                </Tooltip>
              )}
              <Box sx={{ flex: 1 }} />
              {/* Filters, not selections: they change what is LISTED, and a row
                  that is not listed is not sent to. */}
              <FormControlLabel
                sx={{ mr: 0 }}
                control={(
                  <Checkbox
                    size="small"
                    checked={hideCurrent}
                    disabled={currentCount === 0}
                    onChange={(e) => setHideCurrent(e.target.checked)}
                  />
                )}
                label={(
                  <Typography variant="caption">
                    {`Ignore up to date${currentCount ? ` (${currentCount})` : ''}`}
                  </Typography>
                )}
              />
              <FormControlLabel
                sx={{ mr: 0 }}
                control={(
                  <Checkbox
                    size="small"
                    checked={hideOffline}
                    disabled={offline.length === 0}
                    onChange={(e) => setHideOffline(e.target.checked)}
                  />
                )}
                label={(
                  <Typography variant="caption">
                    {`Ignore offline${offline.length ? ` (${offline.length})` : ''}`}
                  </Typography>
                )}
              />
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Tick a row to include it. Every ticked device is marked on the
              server, so it updates itself even if it is asleep now — within
              about 20 minutes if it is merely idle, or on its next power-up if
              it is off. The ones that are online are also told over MQTT and
              start immediately.
            </Typography>

            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360, mb: 1.5 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={allSelected}
                        indeterminate={someSelected && !allSelected}
                        disabled={selectable.length === 0}
                        onChange={toggleAll}
                        inputProps={{ 'aria-label': 'Select all devices' }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Device</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Firmware file</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Built</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Last updated</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groups.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ py: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          Everything is filtered out — untick a box above to see
                          the rest.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {groups.map((g) => {
                    // One section means the title already names it, and a header
                    // repeating the room would be noise above its own rows.
                    const showHeader = groups.length > 1;
                    // Every row, including the offline ones — they are the whole
                    // reason the marks exist.
                    const gRows = g.rows.map((r) => r.uno);
                    const gAll = gRows.length > 0 && gRows.every((u) => selected.has(u));
                    const gSome = gRows.some((u) => selected.has(u));
                    const gOff = g.rows.filter((r) => r.offline).length;
                    return (
                    <React.Fragment key={g.key}>
                      {/* One section header per room. Sticky headers already
                          hold the column names, so this only has to say WHERE
                          the next few rows are. */}
                      {showHeader && (
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={gAll}
                            indeterminate={gSome && !gAll}
                            disabled={gRows.length === 0}
                            onChange={() => toggleGroup(g)}
                            inputProps={{ 'aria-label': `Select all in ${g.title}` }}
                          />
                        </TableCell>
                        <TableCell colSpan={5} sx={{ py: 0.5 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 12 }}
                          >
                            {g.title}
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 1, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}
                            >
                              {`${g.rows.length} device${g.rows.length === 1 ? '' : 's'}`}
                              {gOff > 0 ? ` · ${gOff} offline` : ''}
                            </Typography>
                          </Typography>
                        </TableCell>
                      </TableRow>
                      )}
                      {g.rows.map((r) => {
                    const chip = STATE_CHIP[r.state] || STATE_CHIP.unknown;
                    const checked = selected.has(r.uno);
                    return (
                      <TableRow
                        key={r.uno}
                        hover
                        onClick={() => toggle(r.uno)}
                        sx={{
                          cursor: 'pointer',
                          // Dimmed, not disabled: it CAN be updated, just not
                          // now — the command waits in PendingCommand.
                          opacity: r.offline ? 0.7 : 1,
                        }}
                      >
                        <TableCell padding="checkbox" sx={cell}>
                          <Checkbox
                            size="small"
                            checked={checked}
                            onChange={() => toggle(r.uno)}
                            onClick={(e) => e.stopPropagation()}
                            inputProps={{ 'aria-label': `Update ${r.name}` }}
                          />
                        </TableCell>
                        <TableCell sx={cell}>
                          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                            {r.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                            <Typography variant="caption" color="text.secondary">
                              {r.uno}
                            </Typography>
                            {/* Liveness moved here when the Status column became
                                the FIRMWARE's answer. It no longer decides
                                whether a device is updated, only how soon. */}
                            {r.offline && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                offline
                              </Typography>
                            )}
                            {/* Already carrying an instruction. Without this a
                                device marked a month ago that has never picked
                                it up looks exactly like one nobody has touched,
                                and the answer to "why has it not updated" is on
                                the row itself. */}
                            {(r.marked || r.pendingCommand) && (
                              <Tooltip
                                title={[
                                  r.marked ? `${UPDATE_MARK_COLUMN} is set — it updates on its next Wi-Fi connect, and stays set until a flash succeeds.` : '',
                                  r.pendingCommand ? `Parked for its next poll: “${r.pendingCommand}”.` : '',
                                ].filter(Boolean).join(' ')}
                              >
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  color="info"
                                  sx={{ height: 17, fontSize: 10 }}
                                  label={r.marked && r.pendingCommand ? 'marked + parked'
                                    : (r.marked ? 'marked' : `parked: ${r.pendingCommand}`)}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        {/* Plain monospace, not a link: this is the one string
                            on the sheet that must be read character by character,
                            and link colouring made it the least legible thing
                            on it. The URL is on hover for anyone who wants it. */}
                        <TableCell sx={cell}>
                          {r.binFile ? (
                            <Tooltip title={r.binUrl}>
                              <Typography
                                variant="body2"
                                sx={{ fontFamily: 'monospace', fontSize: 12.5, wordBreak: 'break-all' }}
                              >
                                {r.binFile}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ ...cell, whiteSpace: 'nowrap' }}>
                          <Typography variant="body2">{when(r.builtAt)}</Typography>
                        </TableCell>
                        <TableCell sx={{ ...cell, whiteSpace: 'nowrap' }}>
                          <Typography variant="body2">{when(r.flashedAt)}</Typography>
                        </TableCell>
                        <TableCell sx={cell}>
                          {/* A row reading "Up to date" beside a flash time two
                              minutes OLDER than the build looks like a bug, so
                              say which of the two clocks is responsible. */}
                          <Tooltip
                            title={r.withinTolerance
                              ? `Flashed ${Math.max(1, Math.round((r.builtAt - r.flashedAt) / 60000))}`
                                + ' min before the build was stamped. The upload tool records'
                                + ' the date a few minutes after writing the file, so anything'
                                + ` within ${BUILD_STAMP_TOLERANCE_MS / 60000} minutes counts as the same build.`
                              : ''}
                          >
                            <Chip
                              size="small"
                              label={chip.label}
                              color={chip.color}
                              variant={r.state === STALE ? 'filled' : 'outlined'}
                            />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                    </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* The exact bytes — last look before a fleet reflashes itself.
                Three entries, because they are three different mechanisms with
                three different lifetimes; one combined line would imply the
                same command goes out three times. */}
            <Alert severity="warning" sx={{ mb: 1 }}>
              {allTargets.length > 0 && (
                <>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    {`Written on all ${allTargets.length} selected row${allTargets.length === 1 ? '' : 's'} — picked up on the next poll (~20 min), then cleared on delivery:`}
                  </Typography>
                  <Typography variant="body2" sx={mono}>
                    {`PendingCommand = "${PENDING_UPDATE_COMMAND}"`}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                    Written on the same rows — read on the next Wi-Fi connect,
                    and it stays set until a flash actually succeeds:
                  </Typography>
                  <Typography variant="body2" sx={mono}>
                    {`${UPDATE_MARK_COLUMN} = 1`}
                  </Typography>
                </>
              )}
              {targets.length > 0 && (
                <>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                    {`Published now to the ${targets.length} device${targets.length === 1 ? '' : 's'} that ${targets.length === 1 ? 'is' : 'are'} online:`}
                  </Typography>
                  <Typography variant="body2" sx={mono}>{payload}</Typography>
                </>
              )}
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                They download new firmware and reboot. This cannot be undone.
              </Typography>
            </Alert>

            {/* ⚠️ NO LONGER AN ERROR, and it used to be. "Nothing answered" was
                fatal while MQTT was the only route; now it costs the immediacy
                and nothing else, so it is stated as a fact about timing rather
                than as a failure the operator has to go and fix. */}
            {live.length === 0 && rows.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                {method === 'mqtt'
                  ? `Nothing in this ${scopeLabel} answered, and none of it has reported `
                    + 'to the server in the last minute. They can still be marked — each '
                    + 'will update itself when it next connects — but none will start now.'
                  : 'The broker could not be reached, so this fell back to the last '
                    + 'status the server recorded. The marks are written over REST and '
                    + 'are unaffected; only the immediate publish needs the broker.'}
              </Typography>
            )}

            {/* Nothing selected because everything is current is a different
                outcome from nothing being online, and needs different words:
                there is nothing wrong and nothing to do. */}
            {allTargets.length === 0 && selectable.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                Nothing is selected. Every device shown was updated after its build
                was made — tick one anyway if you are re-flashing one whose update
                failed, since a failed update still records the attempt.
              </Typography>
            )}

            {method === 'status' && live.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Broker unreachable — this list is from the server’s last recorded
                status, not from the devices themselves.
              </Typography>
            )}
          </>
        )}

        {(phase === 'sending' || phase === 'done') && (
          <Box sx={{ py: 1 }}>
            {/* Against `targets`, not `live`: with devices deselected the two
                differ, and a bar that never fills reads as a stall. The marking
                phase is not in the bar — it is concurrent and over in a moment,
                whereas the walk is spaced and is what takes the time. */}
            <LinearProgress
              variant="determinate"
              value={targets.length ? (sent / targets.length) * 100 : 100}
              sx={{ mb: 1 }}
            />
            {/* The two are DIFFERENT PROMISES and are reported separately. The
                marked ones have been instructed; nothing has happened to them
                yet. */}
            <Typography variant="body2">
              {phase === 'sending' && marked === 0
                ? `Marking ${allTargets.length} device${allTargets.length === 1 ? '' : 's'} on the server…`
                : `Marked ${marked} device${marked === 1 ? '' : 's'} — each updates itself when it next connects.`}
            </Typography>
            {markFailed > 0 && (
              <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                {`${markFailed} row${markFailed === 1 ? '' : 's'} could not be written — ${markFailed === 1 ? 'that device is' : 'those devices are'} NOT scheduled. Run this again for ${markFailed === 1 ? 'it' : 'them'}.`}
              </Typography>
            )}
            {targets.length > 0 && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {phase === 'done'
                  ? `Published to ${sent} online device${sent === 1 ? '' : 's'}, which start now.`
                  : `Publishing… ${sent} of ${targets.length}`}
              </Typography>
            )}
            {phase === 'done' && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Devices download and reflash on their own; they will drop offline
                briefly while they do. There is no acknowledgement, so check the
                online dots in a minute or two.
                {queueTargets.length > 0 && ' A device that was asleep picks its update up within about 20 minutes, or on its next power-up if it is switched off — and the mark stays on it until a flash succeeds.'}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {phase === 'confirm' && <Button onClick={onClose}>Cancel</Button>}
        {/* The label names both timings, because they are different promises and
            the operator is agreeing to both: "12 devices · 3 start now". */}
        {phase === 'confirm' && (
          <Button
            variant="contained"
            color="warning"
            disabled={allTargets.length === 0}
            onClick={run}
          >
            {targets.length === allTargets.length
              ? `Update ${allTargets.length} device${allTargets.length === 1 ? '' : 's'}`
              : `Update ${allTargets.length} device${allTargets.length === 1 ? '' : 's'} · ${targets.length} start now`}
          </Button>
        )}
        {phase === 'done' && <Button variant="contained" onClick={onClose}>Close</Button>}
        {phase === 'counting' && <Button onClick={onClose}>Cancel</Button>}
      </DialogActions>
    </Dialog>
  );
}

export { FIRMWARE_UPDATE_COMMAND };
